import { useEffect, useState } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { LuArrowLeftFromLine } from 'react-icons/lu';
import { MdOutlineCancel } from 'react-icons/md';
import { PiMinusCircleBold } from 'react-icons/pi';

import {
  setMinimisePanel,
  setQid
} from '../../../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../../../redux/store/hooks';
import { useGetAllSectionsDataQuery } from '../../../services/startTestAPI/startTestApi';

export type Category = 'section' | 'module' | 'answerType';
export type FilterValue =
  | 'readingWriting'
  | 'math'
  | 'module1'
  | 'module2'
  | 'correct'
  | 'incorrect'
  | 'omitted';

const QuestionsLeftPane = ({ setShowFilters, showFilters }: any) => {
  const {
    completedTestData,
    filters,
    minimisePanel,
    filteredData,
    qId,
    resultById,
    expandedSection,
    testDetailsUnavailable
  } = useAppSelector(state => state.root.startFulltest);
  const dispatch = useAppDispatch();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => {
    const firstSection =
      resultById?.testDTO?.examPatternDTO?.rounds[0]?.sections?.[0];
    return firstSection ? { [firstSection.sectionId]: true } : {};
  });

  useEffect(() => {
    if (expandedSection) {
      setExpandedSections(prev => ({
        ...prev,
        [expandedSection]: true
      }));
    }
  }, [expandedSection]);

  useEffect(() => {
    const firstSection =
      resultById?.testDTO?.examPatternDTO?.rounds[0]?.sections?.[0];
    if (firstSection && (!qId?.questionId || !qId?.sectionId)) {
      const firstQuestion = firstSection.generatedSectionQuestions?.[0];
      if (firstQuestion) {
        dispatch(
          setQid({
            questionId: firstQuestion.id,
            sectionId: firstSection.sectionId,
            questionIndex: 0
          })
        );
        // Ensure section is expanded when question is selected
        setExpandedSections(prev => ({
          ...prev,
          [firstSection.sectionId]: true
        }));
      }
    }
  }, [resultById, dispatch, qId]);

  const { data: sectionsData, error: sectionsError } =
    useGetAllSectionsDataQuery({}, { refetchOnMountOrArgChange: true });

  const getSectionName = (sectionId: string) => {
    const section = sectionsData?.data?.find(
      (section: any) => section.id === sectionId
    );
    const sectionName = section?.sectionName || sectionId;
    return minimisePanel ? `${sectionName.slice(0, 3)}...` : sectionName;
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderChoiceIcon = (q: any) => {
    if (q.isCorrectAnswer) {
      return (
        <IoIosCheckmarkCircleOutline size={24} className='text-[#0B6E4F]' />
      );
    } else if (q.yourChoice === 'Omitted') {
      return (
        <p className='text-[#202020]'>
          <PiMinusCircleBold size={24} />
        </p>
      );
    } else {
      return <MdOutlineCancel size={24} className='text-[#D00000]' />;
    }
  };

  const handleQuestionClick = (
    question: any,
    sectionId: string,
    index: number
  ) => {
    dispatch(
      setQid({ questionId: question.id, sectionId, questionIndex: index })
    );
  };

  return (
    <div
      className={`scroll4 mb-4 h-full justify-start overflow-y-auto rounded-xl border transition-all duration-300 ease-in-out ${
        minimisePanel ? 'min-w-44' : 'md:min-w-[300px]'
      }`}
    >
      <div className='border-appPrimaryColor border-b p-4'>
        <div className='flex items-center justify-between'>
          <span className='text-appPrimaryColor text-[21px] font-medium'>
            {minimisePanel ? 'Qu...' : 'Questions'}
          </span>
          <div className='flex space-x-2'>
            <button
              onClick={() => dispatch(setMinimisePanel(!minimisePanel))}
              className='transform rounded p-1 transition-transform hover:bg-gray-100'
            >
              <LuArrowLeftFromLine
                size={24}
                className={`text-appPrimaryColor font-medium ${
                  minimisePanel ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {testDetailsUnavailable && (
        <div className='p-4 text-sm text-[#646464]'>
          Question review is not available for this account.
        </div>
      )}

      {resultById?.testDTO?.examPatternDTO?.rounds[0]?.sections?.map(
        (section: any) => (
          <div key={section.sectionId}>
            <button
              onClick={() => toggleSection(section.sectionId)}
              className='flex w-full items-center justify-between border-b border-[#646464] px-3 py-4 text-left'
            >
              <span className='text-lg font-medium'>
                {getSectionName(section.sectionId)}
              </span>
              <BiChevronDown
                size={24}
                className={`transform ${
                  expandedSections[section.sectionId] ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSections[section.sectionId] && (
              <div className='pb-4'>
                {section?.generatedSectionQuestions?.map(
                  (question: any, index: number) => {
                    const answer = resultById?.answers[section.sectionId]?.find(
                      (a: any) => a.questionId === question.id
                    );

                    return (
                      <button
                        key={question.id}
                        className={`flex w-full cursor-pointer items-center justify-between space-x-2 rounded border-b border-[#E1E1E1] p-2 px-3 hover:bg-gray-50 
                      ${qId?.questionId === question.id ? 'text-appPrimaryColor bg-blue-50' : 'text-black'}`}
                        onClick={() =>
                          handleQuestionClick(
                            question,
                            section.sectionId,
                            index
                          )
                        }
                      >
                        <span className='text-lg'>Question {index + 1}</span>
                        <div className='flex items-center gap-2'>
                          {answer ? (
                            renderChoiceIcon(answer)
                          ) : (
                            <p className='text-[#202020]'>
                              <PiMinusCircleBold size={24} />
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  }
                )}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default QuestionsLeftPane;
